import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Loader2, Play, RotateCcw, Eye, RefreshCw, FlaskConical } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { openAuthedPdf } from "@/components/admin/AuthImage";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

/**
 * Campaign-side Postcard Studio controls: which template version is
 * pinned, batch generation with progress, and the per-recipient
 * generated snapshot that Test/LIVE sends will upload verbatim.
 */
export default function PostcardStudioCampaignPanel({ campaignId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mock, setMock] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/property-campaigns/${campaignId}/postcard-template`);
      setData(d);
      return d;
    } catch { return null; }
  }, [campaignId]);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  useEffect(() => {
    if (data?.job?.status === "running") {
      timer.current = setTimeout(load, 1500);
    }
    return () => clearTimeout(timer.current);
  }, [data, load]);

  const run = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const c = data.counts || {};
  const job = data.job;
  const running = job?.status === "running";

  return (
    <div className={`${card} mt-6`} data-testid="pc-studio-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider">
          <Layers className="w-4 h-4 inline mr-1" /> Postcard artwork
        </h2>
        <span data-testid="pc-studio-source"
          className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${data.uses_studio ? "bg-cyan-100 text-cyan-900 border border-cyan-300" : "bg-slate-100 text-slate-600 border border-slate-300"}`}>
          {data.assignment_label}
        </span>
        <Link to="/admin/postcard-studio" data-testid="pc-studio-link"
          className="text-xs text-cyan-700 font-semibold underline">Open Postcard Studio</Link>
        {data.uses_studio && (
          <Button size="sm" variant="outline" className="rounded-full ml-auto" data-testid="pc-studio-unassign"
            disabled={busy || running}
            onClick={() => window.confirm("Unassign the Studio template? This campaign reverts to the legacy / default PWS postcard artwork.")
              && run("Template unassigned", () => api.delete(`/admin/property-campaigns/${campaignId}/postcard-template`))}>
            Unassign template
          </Button>
        )}
      </div>

      {!data.uses_studio && (
        <p className="mt-2 text-xs text-slate-600" data-testid="pc-studio-legacy-note">
          This campaign uses the existing code-generated PWS postcard exactly as before.
          To use imported artwork, open Postcard Studio, mark a version <b>Ready</b> and
          assign it to this campaign.
        </p>
      )}

      {data.uses_studio && (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {[
              ["Recipients", data.total_recipients, ""],
              ["Generated", c.generated || 0, "bg-emerald-50 border-emerald-200"],
              ["Requires review", c.requires_review || 0, (c.requires_review ? "bg-amber-50 border-amber-300" : "")],
              ["Failed", c.failed || 0, (c.failed ? "bg-rose-50 border-rose-200" : "")],
              ["Stale version", c.stale || 0, (c.stale ? "bg-violet-50 border-violet-200" : "")],
            ].map(([l, v, tone]) => (
              <div key={l} data-testid={`pc-studio-stat-${l.toLowerCase().replace(/ /g, "-")}`}
                className={`rounded-xl border px-3 py-2 ${tone || "bg-white border-slate-200"}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{l}</div>
                <div className="text-xl font-bold text-slate-900">{v}</div>
              </div>
            ))}
          </div>

          {running && (
            <div className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-900" data-testid="pc-studio-progress">
              <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />
              Generating postcards — {job.done} / {job.total}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full bg-cyan-700 hover:bg-cyan-800" data-testid="pc-studio-generate"
              disabled={busy || running}
              onClick={() => run("Generation started", () => api.post(`/admin/property-campaigns/${campaignId}/generate-postcards`, {}))}>
              <Play className="w-3.5 h-3.5 mr-1" /> Generate Postcards
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="pc-studio-regenerate"
              disabled={busy || running}
              onClick={() => window.confirm("Regenerate ALL postcards for this campaign from the assigned template version? Already-mailed renders are preserved for audit.")
                && run("Regeneration started", () => api.post(`/admin/property-campaigns/${campaignId}/generate-postcards`, { regenerate: true }))}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Regenerate All
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="pc-studio-retry-failed"
              disabled={busy || running || !((c.failed || 0) + (c.requires_review || 0))}
              onClick={() => run("Retry started", () => api.post(`/admin/property-campaigns/${campaignId}/generate-postcards/retry-failed`))}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry Failed
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" data-testid="pc-studio-mock-test"
              disabled={busy || running || !(c.generated || 0)}
              onClick={async () => {
                setBusy(true);
                try {
                  const first = (data.renders || []).find((r) => r.status === "generated");
                  const { data: r } = await api.post(
                    `/admin/property-campaigns/${campaignId}/postcard-test-dry-run`,
                    { invite_id: first?.invite_id });
                  setMock(r);
                  toast.success("Mock test postcard built — nothing was sent to Bing");
                } catch (e) { toast.error(e.response?.data?.detail || "Mock failed"); }
                finally { setBusy(false); }
              }}>
              <FlaskConical className="w-3.5 h-3.5 mr-1" /> Send Mock Test Postcard
            </Button>
            <Button size="sm" variant="outline" className="rounded-full ml-auto" data-testid="pc-studio-refresh"
              disabled={busy} onClick={load}>
              Refresh
            </Button>
          </div>

          <p className="mt-2 text-[11px] text-slate-500" data-testid="pc-studio-note">
            Generating never sends mail. Send Test Postcard and Approve &amp; Send upload the
            exact generated snapshot — nothing is re-rendered at send time.
          </p>

          {mock && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900" data-testid="pc-studio-mock-result">
              <div className="font-bold uppercase tracking-wider">Mock test postcard — nothing sent</div>
              <div className="mt-1 grid sm:grid-cols-2 gap-x-4">
                <div>Reference: <b>{mock.reference}</b></div>
                <div>Bing profile: <b>{mock.profile || "—"}</b></div>
                <div>Held at Bing: <b>{mock.cust_hold ? "yes" : "no"}</b></div>
                <div>Pages: <b>{mock.pdf_pages}</b> · {mock.pdf_bytes} bytes</div>
                <div>Artwork source: <b>{mock.artwork_source}</b></div>
                <div>Uses generated snapshot: <b>{mock.uses_generated_snapshot ? "yes — byte identical" : "no"}</b></div>
              </div>
              <div className="mt-1">Postal panel Bing will print: <b>{(mock.postal || []).filter((l) => typeof l === "string").join(" · ")}</b></div>
              <div className="mt-1 italic">{mock.note}</div>
            </div>
          )}

          {(data.renders || []).length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto border border-slate-200 rounded-xl" data-testid="pc-studio-renders">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <tr><th className="text-left px-2 py-1.5">Artwork address</th>
                    <th className="text-left px-2 py-1.5">Version</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                    <th className="text-left px-2 py-1.5">Proof</th></tr>
                </thead>
                <tbody>
                  {data.renders.map((r) => (
                    <tr key={r.invite_id} className="border-t border-slate-100" data-testid={`pc-studio-render-${r.invite_id}`}>
                      <td className="px-2 py-1.5 text-slate-800">{r.artwork_address || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">V{r.template_version_number}</td>
                      <td className={`px-2 py-1.5 font-semibold ${r.status === "generated" ? "text-emerald-700" : "text-rose-700"}`}>
                        {r.status === "generated" ? "Generated" : r.status === "requires_review" ? "Requires Review" : "Failed"}
                        {r.problems?.length ? ` — ${r.problems[0]}` : ""}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.status === "generated" && (
                          <button className="text-cyan-700 font-semibold hover:text-cyan-900"
                            data-testid={`pc-studio-render-open-${r.invite_id}`}
                            onClick={() => openAuthedPdf(`/admin/property-campaigns/${campaignId}/postcard-renders/${r.invite_id}`).catch((e) => toast.error(e.message))}>
                            <Eye className="w-3 h-3 inline mr-0.5" /> Open
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
